import { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../services/NetworkServices';
import {
  getRole,
  getToken,
  removeApprovalStatus,
  removeEmail,
  removeStageNumber,
  removeToken,
} from '../utils/auth-token-utils/cookie';
import { AuthContext } from '../context/AuthContext';

export const SUPERVISOR_IDLE_TIMEOUT_MS = 5 * 60 * 1000;
export const SUPERVISOR_SESSION_MARKER = 'supervisorSessionActive';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const clearSupervisorSession = (): void => {
  removeToken();
  removeEmail();
  removeStageNumber();
  removeApprovalStatus();
  sessionStorage.removeItem(SUPERVISOR_SESSION_MARKER);
  localStorage.setItem('isLoggedOut', Date.now().toString());
};

export const useSupervisorAutoLogout = (): void => {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const fcmToken = auth?.fcmToken ?? '';
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const performLogout = useCallback(async () => {
    if (!getToken()) return;
    try {
      await logout({ fcmToken });
    } catch (error) {
      console.error('Supervisor auto-logout API error:', error);
    } finally {
      clearSupervisorSession();
      navigate('/signin', { replace: true });
    }
  }, [fcmToken, navigate]);

  useEffect(() => {
    if (getRole() !== 'Supervisor' || !getToken()) return;
    if (sessionStorage.getItem(SUPERVISOR_SESSION_MARKER)) return;

    void performLogout();
  }, [performLogout]);

  useEffect(() => {
    if (getRole() !== 'Supervisor' || !getToken()) return;
    if (!sessionStorage.getItem(SUPERVISOR_SESSION_MARKER)) return;

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        void performLogout();
      }, SUPERVISOR_IDLE_TIMEOUT_MS);
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetIdleTimer, { passive: true });
    });
    resetIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetIdleTimer);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [performLogout]);
};
